// The audible cue schedule: which beats get a sound, and which sound.
//
// Count 1 is the highest sound, 5 the next, every other counted beat a soft tick (DESIGN.md §2, Cues).
// The cues sit on the map's beats, so they follow the tracked tempo, re-anchors and shifts.

import { beatIndexAt, countOfBeat, timeOfBeatIndex, type CountMap } from './grid.js';

/** Which counts get a cue: none, the steps (1 2 3 5 6 7), only 1 and 5, or all eight. */
export type CueMode = 'off' | 'steps' | 'oneFive' | 'all';

/** The sound: `one` the highest, `five` the next, `tick` a soft tick. */
export type CueKind = 'one' | 'five' | 'tick';

export interface Cue {
  /** Time of the beat, in seconds. */
  readonly t: number;
  readonly kind: CueKind;
  /** The count (1..8) of the beat. */
  readonly count: number;
}

const STEPS: ReadonlySet<number> = new Set([1, 2, 3, 5, 6, 7]);

/**
 * The cues in the span [t0, t1), ascending. The span is half-open, so consecutive spans (a scheduler looking
 * a little ahead each tick) neither miss a cue nor repeat one. Past either end of the map's beats the cues
 * continue at the map's median interval, as `countAt` does.
 */
export function cuesBetween(map: CountMap, t0: number, t1: number, mode: CueMode): Cue[] {
  const cues: Cue[] = [];
  if (mode === 'off' || !(t1 > t0)) return cues;
  let i = Math.floor(beatIndexAt(map, t0));
  if (timeOfBeatIndex(map, i) < t0) i++;
  for (let t = timeOfBeatIndex(map, i); t < t1; t = timeOfBeatIndex(map, ++i)) {
    const count = countOfBeat(map, i);
    if (mode === 'oneFive' ? count !== 1 && count !== 5 : mode === 'steps' && !STEPS.has(count)) continue;
    cues.push({ t, kind: count === 1 ? 'one' : count === 5 ? 'five' : 'tick', count });
  }
  return cues;
}
